import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import React, { useState } from "react";
import { ControllerFieldState, UseFormReturn } from "react-hook-form";
import { clientOnboardingSchema, clientOnboardingType } from "../../validator";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { getAppTypesForClient, TemplateAppType } from "@/http";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
// import EditIcon from "@/components/icons/EditIcon";
import { Button } from "@/components/ui/button";
import AIIcon from "@/components/icons/AIIcon";
import { Gem } from "lucide-react";
import { ModalTaskTypeENUM } from "@/types";
import useCurrentUserStore from "@/store";

type PropTypes = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<clientOnboardingType, any, undefined>;
  nextTrigger: VoidFunction;
};

const Step1Content: React.FC<PropTypes> = (props) => {
  const { setSelectedApp } = useCurrentUserStore();
  const step1Data = props.form.watch("step1");

  const [currentItem, setCurrentItem] = useState<TemplateAppType>();
  const { data: appTemplatesListData } = useQuery<
    Awaited<ReturnType<typeof getAppTypesForClient>>
  >({
    queryKey: ["super-admin-apps-types-for-client", 100, 1],
    queryFn: () => getAppTypesForClient(),
    retry: 1,
  });

  // const appTemplateId = step1Data.appTemplateId;
  // const appTemplatesList = appTemplatesListData?.data.data.result.slice(0, 1);

  const handleNextClick = () => {
    const name =
      data.find((item) => item.id === step1Data.appTemplateId)?.name || "";
    step1Data.appName = name;
    const resolve = clientOnboardingSchema.shape.step1.safeParse(step1Data);
    if (resolve.success) {
      props.form.clearErrors();
      props.nextTrigger();
    }
  };

  const data = appTemplatesListData?.data.data.result || [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3">
        <h2 className="font-semibold text-[28px]/[32px]">
          Choose Your AI-Powered App Template
        </h2>
        <p className="text-sm text-muted-foreground">
          Select a template to start building your AI-powered app.
        </p>
      </div>

      {/* App Template Checkbox */}
      <FormField
        control={props.form.control}
        name="step1.appTemplateId"
        defaultValue={step1Data.appTemplateId}
        render={({ field, fieldState }) => (
          <FormItem>
            <RadioGroup
              onValueChange={(e) => {
                const selectedTemplate =
                  appTemplatesListData?.data.data.result.filter(
                    (item) => item.id === e
                  ) || undefined;
                setCurrentItem(selectedTemplate?.[0]);
                step1Data.appTemplateName = selectedTemplate?.[0]?.name;
                step1Data.requiredTemplateModalTypes = selectedTemplate?.[0]
                  ?.config?.modality || [ModalTaskTypeENUM.TTT];
                field.onChange(e);
                setSelectedApp(selectedTemplate?.[0]);
              }}
              defaultValue={field.value}
              className="w-full grid grid-cols-2 gap-4"
            >
              {data.map((_, index) => (
                <TemplateItems
                  key={index}
                  fieldState={fieldState}
                  index={data[index]?.id}
                  selectedId={field?.value}
                  name={data[index]?.name}
                  appData={data[index]}
                />
              ))}
            </RadioGroup>
          </FormItem>
        )}
      />

      {/* next button */}
      {currentItem?.is_external ? (
        <Button
          id="app-creation-step1"
          type="button"
          disabled={currentItem?.is_premium}
          className={cn("w-full", {
            "bg-[#E2E8F0] text-black cursor-not-allowed":
              currentItem?.is_premium,
          })}
          onClick={() => window.open(currentItem?.external_app_url, "_blank")}
        >
          {currentItem?.is_premium ? "Contact Support to buy" : "Go to App"}
        </Button>
      ) : (
        <Button
          id="app-creation-step1"
          type="submit"
          disabled={currentItem?.is_premium}
          className={cn("w-full", {
            "bg-[#E2E8F0] text-black cursor-not-allowed":
              currentItem?.is_premium,
          })}
          onClick={handleNextClick}
        >
          {currentItem?.is_premium ? "Contact Support to buy" : "Next"}
        </Button>
      )}
    </div>
  );
};

export default Step1Content;

const TemplateItems = ({
  index,
  selectedId,
  appData,
  name,
  fieldState,
}: {
  index: string;
  name: string;
  appData: TemplateAppType;
  selectedId: string;
  fieldState: ControllerFieldState;
}) => {
  return (
    <FormLabel
      key={index}
      htmlFor={index}
      className={cn(
        "flex flex-col gap-3 border-2 border-secondary rounded-xl p-3",
        {
          "border-primary": index === selectedId,
          "border-destructive": fieldState.error,
        }
      )}
    >
      {/* radio button */}
      <FormControl>
        <FormItem className="flex justify-between">
          <div className="flex lg:flex-row flex-col-reverse gap-2">
            {!appData.icon ? (
              <AIIcon />
            ) : (
              <img
                src={appData.icon}
                className="h-[28px] lg:h-[32px] w-[28px] lg:w-[32px]"
              />
            )}
            {appData.is_premium && (
              <div className="flex items-center gap-1 bg-[#F1F5F9] rounded-2xl px-2">
                <Gem width={12} height={12} />
                <p className="text-sm">Premium</p>
              </div>
            )}
          </div>
          <FormControl>
            <RadioGroupItem
              value={index}
              id={index}
              className={cn({
                "border-destructive": fieldState.error,
              })}
            />
          </FormControl>
        </FormItem>
      </FormControl>
      {/* name */}
      <span className="font-bold text-sm text-[#0F172A]">{name}</span>
      {/* description */}
      <span className="font-normal text-xs text-[#64748B]">
        {appData.description}
      </span>

      <FormMessage />
    </FormLabel>
  );
};

// const ComingSoonItems = ({
//   index,
//   selectedId,
//   fieldState,
// }: {
//   index: string;
//   selectedId: string;
//   fieldState: ControllerFieldState;
// }) => {
//   return (
//     <label
//       key={index}
//       htmlFor={index}
//       className={cn(
//         "flex flex-col gap-3 border-2 border-secondary rounded-xl p-3",
//         {
//           "border-primary": index === selectedId,
//           "border-destructive": fieldState.error,
//         }
//       )}
//     >
//       {/* radio button */}
//       <FormControl>
//         <FormItem className="flex justify-between">
//           <div className="flex lg:flex-row flex-col-reverse gap-2">
//             <AIIcon />
//             <div className="flex items-center gap-1 bg-[#F1F5F9] rounded-2xl px-2">
//               <Gem width={12} height={12} />
//               <p className="text-sm">Premium</p>
//             </div>
//           </div>
//           <FormControl>
//             {/* <RadioGroupItem value={"coming_soon_" + index} id={index} /> */}
//             <RadioGroupItem value={index} id={index} />
//           </FormControl>
//         </FormItem>
//       </FormControl>
//       {/* name */}
//       <span className="font-bold text-sm text-[#0F172A]">AI Powered App</span>
//       {/* description */}
//       <span className="font-normal text-xs text-[#64748B]">
//         Generate and manage AI-powered apps effortlessly.
//       </span>
//     </label>
//   );
// };
